/*
 * Copyright © 2026 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

import React, { ReactElement, ReactNode, useContext, useEffect, useState } from "react"
import { observer } from "mobx-react-lite"
import { CloudUploadIcon, VolumeOffIcon } from "@heroicons/react/outline"
import { LinearProgress } from "@mui/material"
import VirtualExamContext from "views/exam/virtual-exam/virtual-exam-context"

// Technicians aim for recordings of about this length, so the patient counts down to it. The technician decides
// when to stop, so the countdown is approximate and counts up past zero until they do.
const targetRecordingSeconds = 30

// The upload message stays up at least this long so that a fast upload doesn't just flash on screen.
const minimumUploadMessageMs = 4000

const countdownTickMs = 250

const secondsText = (seconds: number): string => `${seconds} ${seconds === 1 ? "second" : "seconds"}`

/**
 * The patient's view of how long a recording has left: "29 seconds" while counting down to the target length, then
 * "+4 seconds" once the recording runs past it.
 */
const recordingTimeText = (elapsedSeconds: number): { text: string; isOvertime: boolean } => {
  const remainingSeconds = targetRecordingSeconds - elapsedSeconds
  if (remainingSeconds > 0) {
    return { text: secondsText(Math.ceil(remainingSeconds)), isOvertime: false }
  }
  return { text: `+${secondsText(Math.max(1, Math.ceil(-remainingSeconds)))}`, isOvertime: true }
}

/**
 * Returns true while `active` is true and for at least `minimumMs` after it first became true.
 */
const useMinimumDisplay = (active: boolean, minimumMs: number): boolean => {
  const [holdUntil, setHoldUntil] = useState(0)
  const [holding, setHolding] = useState(false)

  useEffect(() => {
    if (active) {
      setHoldUntil(Date.now() + minimumMs)
      setHolding(true)
    }
  }, [active, minimumMs])

  useEffect(() => {
    if (active || !holding) {
      return
    }
    const remainingMs = holdUntil - Date.now()
    if (remainingMs <= 0) {
      setHolding(false)
      return
    }
    const timeout = setTimeout(() => setHolding(false), remainingMs)
    return () => clearTimeout(timeout)
  }, [active, holding, holdUntil])

  return active || holding
}

/**
 * The darkened panel centered over the patient's video, used for every message about the recording. All of its text
 * shares one size and shade so that no part of a message reads as less important than the rest.
 */
const OverlayPanel = ({
  children,
  ...props
}: { children: ReactNode } & React.HTMLAttributes<HTMLDivElement>): ReactElement => {
  return (
    <div
      className="absolute z-10 w-1/2 top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 flex flex-col
    items-center gap-y-2 px-3 py-4 rounded-xl bg-black bg-opacity-60 text-white text-opacity-95 text-xl leading-snug
    text-center"
      {...props}
    >
      {children}
    </div>
  )
}

const overlayIconClassName = "h-16 w-16 opacity-40"

const RecordingCountdownMessage = ({ startedAt }: { startedAt: number }): ReactElement => {
  const [now, setNow] = useState(Date.now)

  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), countdownTickMs)
    return () => clearInterval(interval)
  }, [])

  const { text, isOvertime } = recordingTimeText(Math.max(0, (now - startedAt) / 1000))

  return (
    <OverlayPanel role="timer" aria-live="off">
      <div className="flex items-center gap-x-2">
        <span className="h-2.5 w-2.5 rounded-full bg-red-500 animate-pulse" />
        <span>Recording in progress</span>
      </div>
      <div className="tabular-nums">
        {text}
        {!isOvertime && <div className="text-base">(approximate)</div>}
      </div>
      <div>Please remain still and make as little noise as possible</div>
    </OverlayPanel>
  )
}

const UploadingMessage = ({ progress }: { progress: number }): ReactElement => {
  return (
    <OverlayPanel role="status">
      <CloudUploadIcon className={overlayIconClassName} aria-hidden="true" />
      <div>Complete</div>
      <div>The audio recording is being uploaded.</div>
      <LinearProgress
        variant="determinate"
        value={progress}
        aria-label="Upload progress"
        sx={{
          width: "100%",
          height: "6px",
          marginTop: "6px",
          borderRadius: "3px",
          backgroundColor: "rgba(255, 255, 255, 0.2)",
          "& .MuiLinearProgress-bar": { backgroundColor: "rgba(255, 255, 255, 0.8)", borderRadius: "3px" },
        }}
      />
    </OverlayPanel>
  )
}

const MutedMessage = (): ReactElement => {
  return (
    <OverlayPanel>
      <VolumeOffIcon className={overlayIconClassName} aria-hidden="true" />
      <div>You have been muted while your Medaica Technician reviews the recording.</div>
    </OverlayPanel>
  )
}

/**
 * Tells the patient about the technician's latest recording: a countdown while it records, then a message while it
 * uploads. Nothing is shown when the technician cancels a recording.
 */
const RecordingStatusOverlay = observer((): ReactElement | null => {
  const { virtualExamStore } = useContext(VirtualExamContext)
  const auscultationRequest = virtualExamStore.latestAuscultationRequest
  const showUploadingMessage = useMinimumDisplay(!!auscultationRequest?.isUploading, minimumUploadMessageMs)

  if (auscultationRequest?.state === "recording" && auscultationRequest.recordingStartedAt !== null) {
    return <RecordingCountdownMessage startedAt={auscultationRequest.recordingStartedAt} />
  }
  if (showUploadingMessage) {
    return <UploadingMessage progress={auscultationRequest?.uploadProgress ?? 0} />
  }
  return null
})

export default RecordingStatusOverlay
export {
  MutedMessage,
  RecordingCountdownMessage,
  UploadingMessage,
  minimumUploadMessageMs,
  recordingTimeText,
  targetRecordingSeconds,
  useMinimumDisplay,
}
